<template>
  <VAppBar
    light
    app
    color="white"
    max-height="52"
    height="52"
    class="header box-shadow-elevation-3 d-flex justify-center px-4"
    :class="!isHeaderShown ? 'is-hidden' : ''"
    data-test="checkout-header"
  >
    <VDialog
      v-if="isReservationExpired"
      :value="isReservationExpired"
      width="430"
      persistent
      outlined
      data-test="reservation-expired-dialog"
    >
      <BookingExpiredPopup
        @trackGtmEventReservationExpired="
          $emit('trackGtmEventReservationExpired')
        "
      />
    </VDialog>
    <div
      class="breakpoint-xl d-flex align-items-center"
      :style="$vuetify.breakpoint.xs ? { width: '92%' } : {}"
    >
      <VBtn
        class="mr-2 header__link text-capitalize d-flex align-items-center"
        color="secondary"
        text
        data-test="back-to-form-button"
        @click="$emit('cancelBooking')"
      >
        <div class="d-flex align-items-center">
          <VIcon class="mr-2"> mdi-arrow-left </VIcon>
          <span
            v-if="!this.$vuetify.breakpoint.smAndDown"
            class="body-2 back-btn__text"
            data-test="back-to-form-text"
          >
            {{ $t("checkout.backToFrom") }}
          </span>
        </div>
      </VBtn>
      <div
        class="
          header__features
          overflow-x-auto overflow-y-hidden
          d-flex
          align-items-center
        "
        :class="{ 'py-3 pr-1': $vuetify.breakpoint.xs }"
        data-test="checkout-header-features"
      >
        <Options
          :appConfig="appConfig"
          :rideType="rideType"
          :tripType="tripType"
          :vehicleCategoryName="getVehicleCategoryName"
          :isCheckoutHeader="true"
        />
        <PassengersSummary :adults="adultsCount" :children="infantsCount" />
        <div
          v-for="feature in selectedFeaturesWithRelevantAmount"
          :key="feature.featureName"
          class="d-flex text-nowrap"
          :data-test="`feature-${feature.featureName}`"
        >
          <div
            v-if="feature.icon !== 'mdi-wheelchair-accessibility'"
            class="d-flex align-items-center"
          >
            <img
              v-if="feature.icon !== 'mdi-wheelchair-accessibility'"
              class="feature-logo"
              :class="{ 'feature-logo--show': feature.showIcon }"
              :src="`data:image/svg+xml;base64,${feature.icon}`"
              :alt="$t('imageAlt.featureIcon', { name: feature.featureName })"
              @load="feature.showIcon = true"
            />
            <span class="body-2 mr-4" :data-test="`feature-amount-${feature.featureName}`">
              {{ feature.amount }} {{ feature.featureName }}
            </span>
          </div>
          <div v-else class="d-flex align-items-center text-nowrap mr-4">
            <VIcon> mdi-wheelchair-accessibility </VIcon>
            <span class="body-2" data-test="wheelchair-feature-text"
              >{{ feature.amount }} {{ $t("features.wheelchairPlace") }}</span
            >
          </div>
        </div>
        <div
          v-if="animalsTransportation"
          class="d-flex align-items-center text-nowrap"
          data-test="animals-feature"
        >
          <VIcon> mdi-paw </VIcon>
          <span class="body-2" data-test="animals-feature-text">{{ $t("features.animals") }}</span>
        </div>
      </div>
    </div>

    <VSpacer />
    <VDivider v-if="isAuth && $vuetify.breakpoint.mdAndDown" vertical />
    <div v-if="isAuth && minutes && seconds" class="pl-2">
      <h5 class="heading-5">{{ minutes }}:{{ seconds }}</h5>
    </div>
  </VAppBar>
</template>
<script lang="ts">
export default {
  name: 'SCheckoutHeader',
};
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { PerfectScrollbar } from 'vue2-perfect-scrollbar';
import PaymentTypeEnum from '@slg/web-customer-shared/src/enum/PaymentTypeEnum';
import BookingExpiredPopup from '@slg/web-customer-shared/src/components/checkout/checkoutHeader/BookingExpiredPopup.vue';
import Options from '@slg/web-customer-shared/src/components/common/bookingDetails/Options.vue';
import PassengersSummary from '@slg/web-customer-shared/src/components/common/bookingDetails/PassengersSummary.vue';
import SelectedFeatures from '@slg/web-customer-shared/src/components/common/bookingDetails/SelectedFeatures.vue';
import { useRouteRef, useRouter } from '../../../composables';

const props = defineProps({
  appConfig: {
    type: Object,
    default: null,
  },
  adultsCount: {
    type: Number,
    default: null,
  },
  infantsCount: {
    type: Number,
    default: null,
  },
  animalsTransportation: {
    type: Boolean,
    default: false,
  },
  tripType: {
    type: String,
    default: null,
  },
  rideType: {
    type: String,
    default: null,
  },
  selectedFeatures: {
    type: Array,
    default: () => [],
  },
  vehicleCategories: {
    type: Array,
    default: () => [],
  },
  vehicleCategoryId: {
    type: String,
    default: null,
  },
  temporaryReservationDeadline: {
    type: Number,
    default: null,
  },
  isAuth: {
    type: Boolean,
    default: false,
  },
  remainingTime: {
    type: Number,
    default: null,
  },
  isHeaderShown: {
    type: Boolean,
    default: true,
  },
  paymentType: {
    type: String,
    default: null,
  },
  ignoreRemainingTime: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['trackGtmEventReservationExpired', 'cancelBooking', 'updateRemainingTime']);

const route = useRouteRef();
const router = useRouter();

const minutes = ref(null);
const seconds = ref(null);
const isReservationExpired = ref(false);
const infiniteScrollAvailable = ref(true);
const expiredTimerIntervalId = ref(null);
const selectedFeaturesWithRelevantAmount = ref([]);

const getVehicleCategoryName = computed(() => {
  if (props.vehicleCategories.length === 1) {
    return null;
  }
  const vehicleCategory = props.vehicleCategories.find(
    ({ vehicleCategoryId }) => vehicleCategoryId === props.vehicleCategoryId
  );
  return (
    vehicleCategory.localizedName[route.value.params.lang] ||
    vehicleCategory.localizedName.en ||
    vehicleCategory.identifier
  );
});

function startTimer() {
  emit('updateRemainingTime', props.remainingTime - 1);
  minutes.value = Math.trunc(props.remainingTime / 60).toString();
  const tmpSeconds = (props.remainingTime % 60).toString();
  seconds.value =
    tmpSeconds.length !== 1 ? tmpSeconds : tmpSeconds.padStart(2, '0');
  if (props.remainingTime === 0) {
    clearInterval(expiredTimerIntervalId.value);
    if (
      (props.paymentType === PaymentTypeEnum.PRE_PAID ||
        props.paymentType === PaymentTypeEnum.PRE_AUTHORIZATION) &&
      !props.ignoreRemainingTime
    ) {
      router.go();
    }
    if (!props.ignoreRemainingTime) {
      isReservationExpired.value = true;
    }
  }
}

watch(() => props.temporaryReservationDeadline, async () => {
  if (props.temporaryReservationDeadline) {
    if (props.remainingTime <= 0) {
      isReservationExpired.value = true;
    } else {
      expiredTimerIntervalId.value = setInterval(startTimer, 1000);
    }
  }
}, { deep: true, immediate: true });

watch(() => props.selectedFeatures, () => {
  selectedFeaturesWithRelevantAmount.value = props.selectedFeatures.reduce(
    (results, feature) => {
      if (feature.amount !== 0) {
        results.push({
          featureName:
            feature.localizedVehicleFeatureName[
              route.value.params.lang
            ] ||
            feature.localizedVehicleFeatureName.en ||
            feature.identifier,
          amount: feature.amount,
          icon: feature.svgIconBase64,
          showIcon: false,
        });
      }
      return results;
    },
    []
  );
}, { deep: true, immediate: true });

onBeforeUnmount(() => {
  clearInterval(expiredTimerIntervalId.value);
});
</script>

<style scoped lang="scss">
@import "@slg/web-customer-shared/src/style/headerLinks.scss";

.feature-logo {
  width: 24px;
  height: 24px;
  opacity: 0;

  &--show {
    opacity: 1;
  }
}
</style>
